import { z } from 'zod';
import { UserTypeEnum } from '../enums/auth';
import { isPasswordStrong } from '../validators/password';
import { isValidE164Phone } from '../validators/phone';

export const LoginSchema = z.object({
  email: z.email('Invalid email address'),
  password: z.string(),
});

export const SignUpSchema = z
  .object({
    user_type: z.nativeEnum(UserTypeEnum),
    given_name: z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .trim(),
    email: z.email('Invalid email address'),
    phone_number: z
      .string()
      .min(1, 'Phone number is required')
      .refine(isValidE164Phone, { message: 'Invalid phone number' }),
    password: z
      .string()
      .refine(isPasswordStrong, {
        message:
          'Password must be at least 8 chars and include uppercase, lowercase, number, and special character',
      }),
    confirm_password: z.string().min(1, 'Please confirm your password'),
    terms: z.boolean().refine((value) => value, {
      message: 'You must accept terms and conditions',
    }),
  })
  .refine((value) => value.password === value.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });

export const PasswordResetRequestSchema = z.object({
  email: z.email('Invalid email address'),
});

export const PasswordResetConfirmationSchema = z
  .object({
    email: z.email('Invalid email address'),
    code: z.string().min(1, 'Confirmation code is required'),
    newPassword: z.string().refine(isPasswordStrong, {
      message:
        'Password must be at least 8 chars and include uppercase, lowercase, number, and special character',
    }),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((value) => value.newPassword === value.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });

export const ChangePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().refine(isPasswordStrong, {
      message:
        'Password must be at least 8 chars and include uppercase, lowercase, number, and special character',
    }),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((value) => value.new_password === value.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });

export const AuthUserSchema = z.object({
  email: z.string(),
  email_verified: z.string(),
  phone_number: z.string(),
  phone_number_verified: z.string(),
  family_name: z.string(),
  given_name: z.string(),
  address: z.string(),
  'custom:hq_country': z.string(),
  'custom:operating_countries': z.string(),
  'custom:user_type': z.string(),
  'custom:parent_sub': z.string(),
  'custom:terms_hash': z.string().optional(),
  sub: z.string(),
});

export const TermsAndConditionsRequestSchema = z.object({
  terms_version: z.string(),
  accepted_at: z.string(),
});

export const TermsAndConditionsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  inserted_id: z.number().nullable(),
  updated: z.boolean(),
  cognito_updated: z.boolean(),
  terms_version: z.string(),
  accepted_at: z.string(),
});

export const TermsAndConditionsChecksumSchema = z.object({
  sha256: z.string(),
  computed_at: z.string(),
  file_path: z.string(),
});

export const TermsAndConditionsPresignedUrlSchema = z.object({
  upload_url: z.string(),
  archived_path: z.string(),
});

export type Login = z.infer<typeof LoginSchema>;
export type SignUp = z.infer<typeof SignUpSchema>;
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;
export type PasswordResetConfirmation = z.infer<
  typeof PasswordResetConfirmationSchema
>;
export type ChangePassword = z.infer<typeof ChangePasswordSchema>;

export type AuthUser = z.infer<typeof AuthUserSchema>;

export type TermsAndConditionsRequest = z.infer<
  typeof TermsAndConditionsRequestSchema
>;

export type TermsAndConditionsResponse = z.infer<
  typeof TermsAndConditionsResponseSchema
>;

export type TermsAndConditionsChecksum = z.infer<
  typeof TermsAndConditionsChecksumSchema
>;

export type TermsAndConditionsPresignedUrl = z.infer<
  typeof TermsAndConditionsPresignedUrlSchema
>;
